import { useEffect, useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { useGroupStore } from '@/stores/groupStore'
import {
  Box,
  Typography,
  Button,
  Alert,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link as MuiLink,
  IconButton,
  Menu,
  useMediaQuery,
  Collapse,
  TablePagination,
  Breadcrumbs,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterListIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material'
import { GroupForm } from '@/components/groups/GroupForm'
import { AddMemberModal } from '@/components/groups/AddMemberModal'
import type { StudentGroup, CreateGroupData, UpdateGroupData, AddMemberData, GroupStatus } from '@/types'
import { useEnumsStore } from '@/stores/enumsStore'
import '@/styles/dashboard.css'
import { GroupStatusBadge } from '@/components/groups/GroupStatusBadge'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { ROUTES } from '@/utils/constants'
import { useTheme } from '@mui/material/styles'
import Link from '@mui/material/Link'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { GroupCard } from '@/components/groups/GroupCard'

export function GroupsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    groups,
    isLoading,
    error,
    pagination,
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    addMember,
    archiveGroup,
    restoreGroup,
    clearError,
  } = useGroupStore()

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<StudentGroup | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<GroupStatus | ''>('')
  const [sortBy, setSortBy] = useState<'name' | 'created_at' | 'updated_at' | 'status'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [actionsGroup, setActionsGroup] = useState<StudentGroup | null>(null)

  const enums = useEnumsStore((state) => state.enums)
  const groupStatusOptions = enums?.GroupStatus ?? []

  const [appliedSearch, setAppliedSearch] = useState('')

  const buildFetchParams = (overrides?: { page?: number; per_page?: number }) => ({
    search: appliedSearch || undefined,
    status: statusFilter || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
    page: overrides?.page ?? pagination.currentPage,
    per_page: overrides?.per_page ?? pagination.per_page,
  })

  useEffect(() => {
    const timer = window.setTimeout(() => setAppliedSearch(searchInput.trim()), 300)
    return () => window.clearTimeout(timer)
  }, [searchInput])

  useEffect(() => {
    fetchGroups({
      search: appliedSearch || undefined,
      status: statusFilter || undefined,
      sort_by: sortBy,
      sort_order: sortOrder,
      page: 1,
      per_page: pagination.per_page,
    }).catch((err) => console.error('Failed to fetch groups:', err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearch, statusFilter, sortBy, sortOrder, pagination.per_page])

  const handlePageChange = (_: unknown, newPage: number) => {
    fetchGroups(buildFetchParams({ page: newPage + 1 })).catch(() => {})
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const perPage = parseInt(e.target.value, 10)
    fetchGroups(buildFetchParams({ page: 1, per_page: perPage })).catch(() => {})
  }

  const handleCreateGroup = async (data: CreateGroupData | UpdateGroupData) => {
    try {
      await createGroup(data as CreateGroupData)
      setIsCreateModalOpen(false)
      fetchGroups()
    } catch (err) {
      // Error handled in store
    }
  }

  const handleEditGroup = async (data: UpdateGroupData) => {
    if (!selectedGroup) return
    try {
      await updateGroup(selectedGroup.id, data)
      setIsEditModalOpen(false)
      setSelectedGroup(null)
      fetchGroups()
    } catch (err) {
      // Error handled in store
    }
  }

  const handleDeleteGroup = async (group: StudentGroup) => {
    if (window.confirm(t('groups.deleteConfirm'))) {
      try {
        await deleteGroup(group.id)
        fetchGroups()
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const handleArchiveGroup = async (group: StudentGroup) => {
    if (window.confirm(t('groups.archiveConfirm'))) {
      try {
        await archiveGroup(group.id)
        fetchGroups()
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const handleRestoreGroup = async (group: StudentGroup) => {
    if (window.confirm(t('groups.restoreConfirm'))) {
      try {
        await restoreGroup(group.id)
        fetchGroups()
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const openActionsMenu = (event: React.MouseEvent<HTMLElement>, group: StudentGroup) => {
    event.stopPropagation()
    setActionsAnchorEl(event.currentTarget)
    setActionsGroup(group)
  }

  const closeActionsMenu = () => {
    setActionsAnchorEl(null)
    setActionsGroup(null)
  }

  const handleAddMember = async (data: AddMemberData) => {
    if (!selectedGroup) return
    try {
      await addMember(selectedGroup.id, data)
      setIsAddMemberModalOpen(false)
      setSelectedGroup(null)
      fetchGroups()
    } catch (err) {
      // Error handled in store
    }
  }

  const handleArchive = async () => {
    closeActionsMenu()
    if (!actionsGroup) return
    if (window.confirm(t('groups.archiveConfirm'))) {
      try {
        await archiveGroup(actionsGroup.id)
        fetchGroups()
      } catch (err) {
        // Error handled in store
      }
    }
  }

  const handleRestore = async () => {
    closeActionsMenu()
    if (!actionsGroup) return
    if (window.confirm(t('groups.restoreConfirm'))) {
      try {
        await restoreGroup(actionsGroup.id)
        fetchGroups()
      } catch (err) {
        // Error handled in store
      }
    }
  }

  return (
    <DashboardLayout>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link component={RouterLink} to={ROUTES.DASHBOARD} underline="hover" sx={{ display: 'flex', alignItems: 'center' }}>
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('dashboard.groups')}</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 3 }}>
          <Typography variant="h4" component="h1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {t('groups.title')}
          </Typography>
          <PageHelpPopover pageKey="groups" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder={t('groups.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: isMobile ? 120 : 200, flex: isMobile ? 1 : undefined }}
          />
          <FormControl size="small" sx={{ minWidth: isMobile ? 140 : 200 }}>
            <InputLabel>{t('groups.sortBy')}</InputLabel>
            <Select
              value={`${sortBy}_${sortOrder}`}
              label={t('groups.sortBy')}
              onChange={(e) => {
                const [by, order] = (e.target.value as string).split('_') as [typeof sortBy, 'asc' | 'desc']
                setSortBy(by)
                setSortOrder(order)
              }}
            >
              <MenuItem value="name_asc">{t('groups.sort_name_asc')}</MenuItem>
              <MenuItem value="name_desc">{t('groups.sort_name_desc')}</MenuItem>
              <MenuItem value="created_at_desc">{t('groups.sort_created_at_desc')}</MenuItem>
              <MenuItem value="created_at_asc">{t('groups.sort_created_at_asc')}</MenuItem>
              <MenuItem value="updated_at_desc">{t('groups.sort_updated_at_desc')}</MenuItem>
              <MenuItem value="updated_at_asc">{t('groups.sort_updated_at_asc')}</MenuItem>
              <MenuItem value="status_asc">{t('groups.sort_status_asc')}</MenuItem>
              <MenuItem value="status_desc">{t('groups.sort_status_desc')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            size="small"
            variant={filtersOpen ? 'contained' : 'outlined'}
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen((o) => !o)}
            sx={{ height: 40 }}
          >
            {t('groups.filtersButton')}
          </Button>
          <Box sx={{ ml: isMobile ? 0 : 'auto', display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsCreateModalOpen(true)}
              sx={{ height: 40 }}
            >
              {t('groups.create')}
            </Button>
          </Box>
        </Box>

        <Collapse in={filtersOpen}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 200 } }}>
              <InputLabel>{t('groups.status')}</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as GroupStatus | '')}
                label={t('groups.status')}
              >
                <MenuItem value="">{t('groups.allStatuses')}</MenuItem>
                {groupStatusOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Collapse>

        {/* Список групп */}
        {isLoading ? (
          isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={i}>
                  <CardContent>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1, my: 1 }} />
                    <Skeleton variant="text" width="90%" />
                    <Skeleton variant="text" width="40%" />
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('groups.name')}</TableCell>
                  <TableCell>{t('groups.status')}</TableCell>
                  <TableCell>{t('groups.description')}</TableCell>
                  <TableCell>{t('groups.members')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4, 5].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                    <TableCell><Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                    <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="text" width="60%" />
                      <Skeleton variant="text" width="50%" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )
        ) : groups.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('groups.empty')}
              </Typography>
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setIsCreateModalOpen(true)}
                  sx={{ height: 40 }}
                >
                  {t('groups.createFirst')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <>
            {isMobile ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {groups.map((group) => (
                  <GroupCard
                    key={group.id}
                    group={group}
                    onEdit={(g) => {
                      setSelectedGroup(g)
                      setIsEditModalOpen(true)
                    }}
                    onAddMember={(g) => {
                      setSelectedGroup(g)
                      setIsAddMemberModalOpen(true)
                    }}
                    onArchive={handleArchiveGroup}
                    onRestore={handleRestoreGroup}
                    onDelete={handleDeleteGroup}
                  />
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('groups.name')}</TableCell>
                      <TableCell>{t('groups.status')}</TableCell>
                      <TableCell>{t('groups.description')}</TableCell>
                      <TableCell>{t('groups.members')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groups.map((group) => (
                      <TableRow key={group.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MuiLink
                              component={RouterLink}
                              to={`/groups/${group.id}`}
                              underline="hover"
                              sx={{ fontSize: 14, fontWeight: 700 }}
                            >
                              {group.name}
                            </MuiLink>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <GroupStatusBadge status={group.status} label={group.status_label} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {group.description || t('groups.noDescription')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {t('groups.membersCount')}: {group.members_count}
                          </Typography>
                          {group.members && group.members.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {group.members
                                .map((m) => m.student)
                                .filter(Boolean)
                                .map((student) => {
                                  const label =
                                    student!.first_name && student!.surname
                                      ? `${student!.surname} ${student!.first_name}`
                                      : student!.email
                                  return (
                                    <MuiLink
                                      key={student!.id}
                                      component={RouterLink}
                                      to={`/users/${student!.id}`}
                                      underline="hover"
                                      sx={{ fontSize: 14 }}
                                    >
                                      {label}
                                    </MuiLink>
                                  )
                                })}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t('groups.noMembers')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={(e) => openActionsMenu(e, group)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                mt: 2,
                ...(isMobile && { flexDirection: 'column', alignItems: 'stretch' }),
              }}
            >
              <TablePagination
                component="div"
                count={pagination.total}
                page={Math.max(0, pagination.currentPage - 1)}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 15, 25, 50]}
                labelRowsPerPage={isMobile ? '' : t('groups.rows_per_page')}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}–${to} ${t('groups.of')} ${count !== -1 ? count : `${t('groups.more_than')} ${to}`}`
                }
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'auto',
                  ...(isMobile && { borderTop: 1, borderColor: 'divider', pt: 1 }),
                }}
              />
            </Box>
          </>
        )}

        <Menu
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={closeActionsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (!actionsGroup) return
              closeActionsMenu()
              navigate(`/groups/${actionsGroup.id}`)
            }}
          >
            <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.view')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!actionsGroup) return
              setSelectedGroup(actionsGroup)
              setIsEditModalOpen(true)
              closeActionsMenu()
            }}
          >
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.edit')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (!actionsGroup) return
              setSelectedGroup(actionsGroup)
              setIsAddMemberModalOpen(true)
              closeActionsMenu()
            }}
          >
            <PersonAddIcon sx={{ mr: 1 }} fontSize="small" />
            {t('groups.addMember')}
          </MenuItem>
          {actionsGroup?.status === 'active' ? (
            <MenuItem onClick={handleArchive}>
              <ArchiveIcon sx={{ mr: 1 }} fontSize="small" />
              {t('groups.archive')}
            </MenuItem>
          ) : (
            <MenuItem onClick={handleRestore}>
              <UnarchiveIcon sx={{ mr: 1 }} fontSize="small" />
              {t('groups.restore')}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              if (!actionsGroup) return
              const groupToDelete = actionsGroup
              closeActionsMenu()
              handleDeleteGroup(groupToDelete)
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            {t('common.delete')}
          </MenuItem>
        </Menu>

        {/* Модальное окно создания */}
        <Dialog
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('groups.create')}</DialogTitle>
          <DialogContent>
            <GroupForm
              onSubmit={handleCreateGroup}
              onCancel={() => setIsCreateModalOpen(false)}
              isLoading={isLoading}
            />
          </DialogContent>
        </Dialog>

        {/* Модальное окно редактирования */}
        <Dialog
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false)
            setSelectedGroup(null)
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{t('groups.edit')}</DialogTitle>
          <DialogContent>
            {selectedGroup && (
              <GroupForm
                initialValues={selectedGroup}
                onSubmit={handleEditGroup}
                onCancel={() => {
                  setIsEditModalOpen(false)
                  setSelectedGroup(null)
                }}
                isLoading={isLoading}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Модальное окно добавления участника */}
        {selectedGroup && (
          <AddMemberModal
            open={isAddMemberModalOpen}
            existingMemberIds={selectedGroup.members?.map((m) => m.student?.id).filter((id): id is number => typeof id === 'number') || []}
            onSubmit={handleAddMember}
            onCancel={() => {
              setIsAddMemberModalOpen(false)
              setSelectedGroup(null)
            }}
          />
        )}
      </Box>
    </DashboardLayout>
  )
}
